import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import GoogleSignIn from './GoogleSignIn';
import AppleSignIn from './AppleSignIn';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
  onClose?: () => void;
  onLoginSuccess?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin, onClose, onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { register, googleLogin, appleLogin, updateLocation } = useAuth();

  // Scroll focused input into view when mobile keyboard opens
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    if (!displayName.trim()) {
      setError('Display name is required');
      setLoading(false);
      return;
    }

    const result = await register(email, password, displayName.trim());
    
    if (result.success) {
      // Save location if provided (best-effort, don't block on failure)
      if (location.trim()) {
        updateLocation(location.trim()).catch(() => {});
      }
      if (onLoginSuccess) onLoginSuccess();
      else if (onClose) onClose();
    } else {
      setError(result.error || 'Registration failed');
    }
    
    setLoading(false);
  };

  const handleGoogleSuccess = async (credential: string) => {
    setLoading(true);
    setError('');

    const result = await googleLogin(credential);
    
    if (result.success) {
      if (onLoginSuccess) onLoginSuccess();
      else if (onClose) onClose();
    } else {
      setError(result.error || 'Google registration failed');
    }
    
    setLoading(false);
  };

  const handleGoogleError = (error: string) => {
    setError(error);
  };

  const handleAppleSuccess = async (response: any) => {
    setLoading(true);
    setError('');

    const result = await appleLogin(response.identityToken, response.user);
    
    if (result.success) {
      if (onLoginSuccess) onLoginSuccess();
      else if (onClose) onClose();
    } else {
      setError(result.error || 'Apple registration failed');
    }
    
    setLoading(false);
  };

  const handleAppleError = (error: string) => {
    setError(error);
  };

  return (
    <div className="auth-form">
      <h2>Create Account</h2>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={handleInputFocus}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="displayName">Display Name</label>
          <input
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            onFocus={handleInputFocus}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="location">City / Region <span style={{ fontWeight: 'normal', color: 'var(--color-text-muted, #888)' }}>(optional)</span></label>
          <input
            type="text"
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            onFocus={handleInputFocus}
            placeholder="e.g. Portland, OR"
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={handleInputFocus}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onFocus={handleInputFocus}
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="submit-btn register-btn"
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>

      <div className="auth-divider">
        <span>or</span>
      </div>

      <AppleSignIn
        onSuccess={handleAppleSuccess}
        onError={handleAppleError}
        text="Sign up with Apple"
      />

      <GoogleSignIn
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
        text="Sign up with Google"
      />

      <div className="switch-mode">
        <p>
          Already have an account?{' '}
          <button onClick={onSwitchToLogin}>
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default RegisterForm;
